import { api } from "./api";

export type CustomerType = "INSTITUTION" | "COMPANY" | "INDIVIDUAL";

export interface CustomerAddress {
  id: string;
  customerId: string;
  label?: string | null;
  address: string;
  postalCode?: string | null;
  city?: string | null;
  zoneId: string | null;
  zone?: { id: string; name: string; zoneNumber: number; deliveryCost?: number | null } | null;
  isDefault: boolean;
  isBilling?: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** Préférence protéines : FISH | MEAT | BOTH | VEGETARIAN */
export type ProteinPreference = "FISH" | "MEAT" | "BOTH" | "VEGETARIAN";

/** Règle de réduction entreprise (pour affichage fiche client) */
export interface EnterpriseDiscountRuleDisplay {
  id: string;
  label: string;
  indexMin: number;
  indexMax: number;
  discountType: "FIXED" | "PERCENTAGE";
  discountValue: number;
  minChargeAmount: number;
  fixedDeliveryPrice?: number | null;
  displayOrder: number;
}

export interface Customer {
  id: string;
  /** Prénom du client (particulier) ou contact principal (institution/entreprise) */
  firstName?: string | null;
  /** Nom de famille (particulier) ou dénomination (institution/entreprise) — obligatoire */
  lastName: string;
  type: CustomerType;
  enterpriseId?: string | null;
  enterprise?: {
    id: string;
    name: string;
    discountRules?: EnterpriseDiscountRuleDisplay[];
  };
  /** Indice client (pour appliquer la règle de réduction de l'entreprise) */
  indice?: number | null;
  addresses?: CustomerAddress[];
  /** Adresse par défaut (aplatie pour les listes / API) */
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  phoneDialCode?: string;
  email?: string | null;
  contactPerson?: string | null;
  notes?: string | null;
  /** Préférences de commande */
  takesStarter?: boolean | null;
  takesMain?: boolean | null;
  takesDessert?: boolean | null;
  proteinPreference?: ProteinPreference | null;
  prefersPremium?: boolean | null;
  prefersVegetarian?: boolean | null;
  /** Réduction (€) appliquée sur la première commande du jour */
  discountAmount?: number | string | null;
  /** Mode de paiement par défaut : En compte / Comptant */
  defaultPaymentMode?: "EN_COMPTE" | "COMPTANT" | null;
  /** Allergènes / intolérances (liés à l'entité Allergène) */
  allergens?: { allergen: { id: string; name: string; code: string; icon: string | null } }[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomerData {
  firstName?: string | null;
  lastName: string;
  type: CustomerType;
  enterpriseId?: string;
  /** Indice client (pour appliquer la règle de réduction de l'entreprise) */
  indice?: number | null;
  phoneDialCode?: string;
  phone?: string;
  email?: string;
  contactPerson?: string;
  notes?: string;
  isActive?: boolean;
  takesStarter?: boolean;
  takesMain?: boolean;
  takesDessert?: boolean;
  proteinPreference?: ProteinPreference | null;
  prefersPremium?: boolean;
  prefersVegetarian?: boolean;
  /** Réduction (€) appliquée sur la première commande du jour (déprécié si entreprise + indice) */
  discountAmount?: number | null;
  /** Mode de paiement par défaut */
  defaultPaymentMode?: "EN_COMPTE" | "COMPTANT" | null;
  allergenIds?: string[];
}

export interface CreateCustomerAddressData {
  label?: string | null;
  address: string;
  postalCode?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  isDefault?: boolean;
  isBilling?: boolean;
  displayOrder?: number;
}

export interface UpdateCustomerAddressData extends Partial<CreateCustomerAddressData> {}

export interface UpdateCustomerData extends Partial<CreateCustomerData> {}

/**
 * Service pour la gestion des clients
 */
export const customerService = {
  /**
   * Récupère tous les clients
   */
  async getAll(includeInactive = false, zoneId?: string): Promise<Customer[]> {
    const params: Record<string, string> = {};
    if (includeInactive) params.includeInactive = "true";
    if (zoneId) params.zoneId = zoneId;
    const response = await api.get<{ success: boolean; data: Customer[] }>(
      "/customers",
      { params }
    );
    return response.data?.data ?? [];
  },

  /**
   * Récupère un client par son ID
   */
  async getById(id: string): Promise<Customer> {
    const response = await api.get<{ success: boolean; data: Customer }>(
      `/customers/${id}`,
    );
    return response.data.data;
  },

  /**
   * Crée un nouveau client
   */
  async create(data: CreateCustomerData): Promise<Customer> {
    const response = await api.post<{ success: boolean; data: Customer }>(
      "/customers",
      data,
    );
    return response.data.data;
  },

  /**
   * Met à jour un client
   */
  async update(id: string, data: UpdateCustomerData): Promise<Customer> {
    const response = await api.put<{ success: boolean; data: Customer }>(
      `/customers/${id}`,
      data,
    );
    return response.data.data;
  },

  /**
   * Supprime un client
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/customers/${id}`);
  },

  /**
   * Liste les adresses d'un client
   */
  async getAddresses(customerId: string): Promise<CustomerAddress[]> {
    const res = await api.get<{ success: boolean; data: CustomerAddress[] }>(
      `/customers/${customerId}/addresses`
    );
    return res.data.data;
  },

  /**
   * Crée une adresse pour un client
   */
  async createAddress(
    customerId: string,
    data: CreateCustomerAddressData
  ): Promise<CustomerAddress> {
    const res = await api.post<{ success: boolean; data: CustomerAddress }>(
      `/customers/${customerId}/addresses`,
      data
    );
    return res.data.data;
  },

  /**
   * Met à jour une adresse
   */
  async updateAddress(
    customerId: string,
    addressId: string,
    data: UpdateCustomerAddressData
  ): Promise<CustomerAddress> {
    const res = await api.put<{ success: boolean; data: CustomerAddress }>(
      `/customers/${customerId}/addresses/${addressId}`,
      data
    );
    return res.data.data;
  },

  /**
   * Supprime une adresse
   */
  async deleteAddress(customerId: string, addressId: string): Promise<void> {
    await api.delete(`/customers/${customerId}/addresses/${addressId}`);
  },

  /**
   * Importe des clients depuis un fichier CSV (contenu envoyé en texte).
   * Colonnes : name, type, zoneNumber (ou zoneId), phone?, email?, contactPerson?, notes?
   */
  async importCsv(csvContent: string): Promise<{
    created: number;
    errors: { row: number; message: string }[];
  }> {
    const response = await api.post<{
      success: boolean;
      data: { created: number; errors: { row: number; message: string }[] };
    }>("/customers/import-csv", { csv: csvContent });
    return response.data.data;
  },
};
